'use client'

import { Radio as RadioPrimitive } from '@base-ui/react/radio'
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group'
import { cva, VariantProps } from 'class-variance-authority'
import { CircleIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@eigtech/ui/lib/utils'

type RadioSize = 'sm' | 'md' | 'lg'

// Define variants for the RadioGroupItem using cva.
const radioItemVariants = cva(
  `border-input text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 shadow-xs peer aspect-square shrink-0 rounded-full border outline-none transition-[color,box-shadow] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 disabled:[&+[data-slot=label]]:opacity-50`,
  {
    variants: {
      size: {
        sm: 'size-3 [&_svg]:size-1.5',
        md: 'size-4 [&_svg]:size-2',
        lg: 'size-5 [&_svg]:size-2.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

// Create a context to pass the size down to items.
const RadioGroupContext = React.createContext<{
  size: RadioSize
}>({ size: 'md' })

function RadioGroup({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive> & { size?: RadioSize }) {
  return (
    <RadioGroupContext.Provider value={{ size }}>
      <RadioGroupPrimitive
        className={cn('grid gap-2', className)}
        data-slot="radio-group"
        {...props}
      />
    </RadioGroupContext.Provider>
  )
}

function RadioGroupItem({
  className,
  size,
  ...props
}: Omit<React.ComponentProps<typeof RadioPrimitive.Root>, 'children'> &
  VariantProps<typeof radioItemVariants>) {
  // Use the size from context if not provided at the item level.
  const { size: contextSize } = React.useContext(RadioGroupContext)
  const effectiveSize = size ?? contextSize

  return (
    <RadioPrimitive.Root
      className={cn(radioItemVariants({ size: effectiveSize }), className)}
      data-slot="radio-group-item"
      {...props}
    >
      <RadioPrimitive.Indicator
        className="relative flex h-full items-center justify-center"
        data-slot="radio-group-indicator"
      >
        <CircleIcon className="fill-primary absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
      </RadioPrimitive.Indicator>
    </RadioPrimitive.Root>
  )
}

export { RadioGroup, RadioGroupItem }
